// Desk build only, inside Route Desk's panel: once a mobile-money payment
// for an order is sent, ask the server every 10 s whether it went through,
// until the answer is definite or the time is up. The rules (when to stop,
// what counts as paid, what blocks Pay) are plain functions in
// util/helpers/deskPaymentStatus.js; this file only runs them.
//
// Outside a frame none of this runs, and the phone app's own check
// (checkOrderPaymentStatus in ./index.js) is left exactly as on master.

import { call, delay, fork, put, race, select, take, takeEvery } from "redux-saga/effects";
import { message } from "antd";
import { Creators, Types } from "../../redux/payments/actions";
import { orderPaymentStatusCheck as orderPaymentStatusCheckApi } from "../../api/payments";
import { isInRouteDeskFrame } from "../../../util/helpers/deskPanelLook";
import { checkAuth } from "../../../util/helpers/requireAuthentication";
import { CURRENT_URL } from "../../../util/helpers/reusableFunctions";
import {
  POLL_INTERVAL_MS,
  loadAll,
  loadOne,
  needsPoller,
  newInFlightRecord,
  nextPollStep,
  paymentOutcome,
  removeOne,
  resultPath,
  resumeDecision,
  saveOne,
  updateOne,
} from "../../../util/helpers/deskPaymentStatus";

// A status check that has not answered after this long counts as failed,
// so a hanging request cannot stretch the 10-minute cap.
export const CHECK_TIMEOUT_MS = 20 * 1000;
// No answer to the payment request itself after this long: start checking
// the order anyway (the entry, and so the Pay block, is already saved).
export const PAYMENT_ANSWER_WAIT_MS = 60 * 1000;
// How often each tab looks for a payment nobody is checking any more.
export const ORPHAN_SCAN_MS = 15 * 1000;
export const STILL_NO_ANSWER = "Still no answer. Check Orders.";
export const STILL_NOT_CONFIRMED = "The order is still not confirmed. Check Orders.";

// A real mobile-money payment for an order (not an invoice, not the "test"
// method, which never reaches a provider).
export const isDeskOrderPayment = (data) =>
  !!data && !!data.order_id && !data.invoice_id && data.payment_method !== "test";

// The fields the backend checks BEFORE it asks the provider (serializer
// field checks, validate_order_details, the view's own "error" answers).
const REFUSAL_KEYS = ["order_id", "amount", "phone_number", "payment_method", "invoice_id", "error"];

// The payment request failed. True only when the backend clearly refused it
// before reaching the provider: a 401 (never reached the payment view), or
// a 400 whose answer is a plain object with only those keys. Not the "pending transaction" refusal (a payment for
// this order is already waiting: check it). Anything else - a bare list
// (the provider call failed or answered with an error), a 5xx, a timeout,
// no answer - may have reached the CHE's phone, so the order is checked.
export const paymentRefused = (failure) => {
  // 401: refused at sign-in, before the payment view; the app's own 401
  // handling (Sign In / refresh) takes over.
  if (failure && (failure.status === 401 || failure.unreached === true)) return true;
  if (!failure || failure.status !== 400) return false;
  const body = failure.body;
  if (!body || typeof body !== "object" || Array.isArray(body)) return false;
  const keys = Object.keys(body);
  if (!keys.length || !keys.every((k) => REFUSAL_KEYS.includes(k))) return false;
  return !isPendingTransaction(body);
};

// The backend's "a payment for this order is already waiting" answer, on
// the order_id key, in English or French (backend src/locale/fr):
//   "You still have a pending transaction under this order/invoice ..."
//   "Vous avez toujours une transaction en attente pour cette commande/facture. ..."
export const isPendingTransaction = (body) =>
  !!body && body.order_id !== undefined &&
  /pending transaction|transaction en attente/i.test(JSON.stringify(body.order_id));

// This tab's id, kept over its own reloads (see needsPoller).
const TAB_ID_KEY = "he-desk-tab-id";
let fallbackTabId = null;
export const getTabId = () => {
  try {
    let id = window.sessionStorage.getItem(TAB_ID_KEY);
    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      window.sessionStorage.setItem(TAB_ID_KEY, id);
    }
    return id;
  } catch (e) {
    if (!fallbackTabId) fallbackTabId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    return fallbackTabId;
  }
};

// What became of the entry while this poll waited:
//   "checking"  still to be checked by this poll
//   "paid"      another poller (tab) saw it paid: show that, don't vanish
//   "unconfirmed"  same, paid but the order not confirmed
//   "gone"      removed (failed seen elsewhere, sign-out, dropped)
// A "Check again now" on an unconfirmed marker is the one case where a
// paid entry is still to be checked.
const entryState = (record, once) => {
  const current = loadOne(record.orderId);
  if (!current) return "gone";
  if (!current.paid) return "checking";
  if (current.unconfirmed) return once ? "checking" : "unconfirmed";
  return "paid";
};

// Ends the poll when another tab already decided; true when it did.
function* settledElsewhere(record, once, errors) {
  const state = entryState(record, once);
  if (state === "checking") return false;
  if (state === "gone") {
    yield put(Creators.deskPaymentClear(record.orderId));
  } else {
    const current = loadOne(record.orderId);
    yield put(Creators.deskPaymentSet({ ...current, phase: state, errors, navigating: false }));
  }
  return true;
}

// One poll per order per tab.
const polling = new Set();

// once: a single "Check again now" check of a payment whose checking ended.
export function* deskPaymentPoll({ record, resumed, once = false }) {
  if (polling.has(record.orderId)) return;
  polling.add(record.orderId);
  try {
    yield* pollUntilKnown(record, resumed, once);
  } finally {
    polling.delete(record.orderId);
  }
}

function* pollUntilKnown(record, resumed, once) {
  let unconfirmedSince = null;
  let errors = 0;
  if (once) {
    const phase = record.unconfirmed ? "unconfirmed" : (record.endPhase || "timeout");
    yield put(Creators.deskPaymentSet({ ...record, phase, errors, checkingAgain: true }));
  } else {
    // A resumed payment whose unclear end the officer already closed stays
    // quiet during its one last check (no waiting screen flashing up).
    yield put(Creators.deskPaymentSet({ ...record, phase: record.finalShown ? "timeout" : "checking", errors }));
    if (!resumed) yield delay(POLL_INTERVAL_MS);
  }
  while (true) {
    if (yield* settledElsewhere(record, once, errors)) return;
    // Tell other tabs this one is checking it.
    updateOne(record.orderId, { pollerId: getTabId(), polledAt: Date.now() });
    let outcome = "skipped";
    let body = null;
    // Signed out: don't ask (the 401 handler would send the page to Sign
    // In); the clock keeps running, so it still ends at the cap.
    if (checkAuth()) {
      try {
        const { response, timedOut } = yield race({
          response: call(orderPaymentStatusCheckApi, { order_id: record.orderId }),
          timedOut: delay(CHECK_TIMEOUT_MS),
        });
        // The app's 401 handler resolves with nothing instead of throwing.
        if (timedOut || !response) {
          errors += 1;
          outcome = "error";
        } else {
          body = response.data;
          outcome = paymentOutcome(body);
        }
      } catch (e) {
        errors += 1;
        outcome = "error";
      }
    }
    if (yield* settledElsewhere(record, once, errors)) return;
    const step = nextPollStep({ outcome, now: Date.now(), startedAt: record.startedAt, unconfirmedSince });
    // "Failed" after the provider already said "successful" (an unconfirmed
    // entry, or "paid, not confirmed" seen earlier in this poll).
    if (step.done === "failed" && (record.unconfirmed || unconfirmedSince !== null)) {
      if (!once) yield put(Creators.resetPayment());
      // "Failed" after the provider already said "successful": the money may
      // still have been taken. Keep the block; Orders is the place to look.
      yield* keepAsUnconfirmed(record, errors, { checkedAgain: once });
      if (once) message.info(STILL_NOT_CONFIRMED);
      return;
    }
    if (step.done === "paid" || step.done === "failed") {
      const cart = yield select((state) => state.cart);
      const cartCheId = cart?.ordering_for?.odoo_id;
      const cartHasLines = Array.isArray(cart?.order) && cart.order.length > 0;
      const path = resultPath(step.done, body, record.orderName);
      // The app's own result screens, only when the cart is still this
      // CHE's and not empty (they show nothing on an empty cart, and their
      // "Home" button empties the cart) and the order's name is known;
      // otherwise the panel's own screen.
      const navigating = (record.cheId === null || String(cartCheId) === String(record.cheId)) && cartHasLines && !!path;
      const current = loadOne(record.orderId) || record;
      if (step.done === "paid") {
        // Confirmed-paid marker, wherever it was seen: no new order for this
        // CHE for PAID_BLOCK_MS (deskPayBlock).
        // Not an unclear end any more: nothing to re-show after a reload.
        saveOne({ ...current, paid: true, unconfirmed: false, paidAt: Date.now(), ended: false, endPhase: null, finalShown: false });
      } else {
        removeOne(record.orderId);
      }
      // Set first, so Route Desk hears "paid"/"failed" before the page changes.
      yield put(Creators.deskPaymentSet({ ...current, ended: false, endPhase: null, phase: step.done, errors, navigating }));
      if (navigating) window.location.assign(`${CURRENT_URL}${path}`);
      return;
    }
    if (once && (outcome === "paid_unconfirmed" || record.unconfirmed)) {
      // Paid, order still not confirmed (or no answer about an unconfirmed
      // one): the money was taken, the block stays.
      yield* keepAsUnconfirmed(record, errors, { checkedAgain: true });
      message.info(outcome === "paid_unconfirmed" ? STILL_NOT_CONFIRMED : STILL_NO_ANSWER);
      return;
    }
    if (once) {
      // Still no definite answer: back to the ended state, quietly.
      const current = loadOne(record.orderId) || record;
      updateOne(record.orderId, { ended: true, endPhase: "timeout" });
      yield put(Creators.deskPaymentSet({ ...current, ended: true, endPhase: "timeout", phase: "timeout", errors, checkedAgainAt: Date.now() }));
      message.info(STILL_NO_ANSWER);
      return;
    }
    if (step.wait) {
      unconfirmedSince = step.unconfirmedSince;
      yield put(Creators.deskPaymentSet({ ...record, phase: step.phase, errors }));
      yield delay(step.wait);
      continue;
    }
    if (step.done === "unconfirmed") {
      yield put(Creators.resetPayment());
      yield* keepAsUnconfirmed(record, errors);
      return;
    }
    // "timeout": keep the entry (marked ended), so Pay stays blocked for
    // this CHE until it is old enough to drop.
    updateOne(record.orderId, { ended: true, endPhase: step.done });
    yield put(Creators.resetPayment());
    yield put(Creators.deskPaymentSet({ ...record, ended: true, endPhase: step.done, phase: step.done, errors }));
    return;
  }
}

// "Paid, order not confirmed": the money was taken. Kept as an unconfirmed
// paid marker: that CHE stays blocked until a check finds the order
// confirmed, or UNCONFIRMED_BLOCK_MS after it was first seen.
function* keepAsUnconfirmed(record, errors, { checkedAgain = false } = {}) {
  const current = loadOne(record.orderId) || record;
  const paidAt = current.unconfirmed && current.paidAt ? current.paidAt : Date.now();
  const marker = { ...current, paid: true, unconfirmed: true, paidAt, ended: true, endPhase: "unconfirmed" };
  saveOne(marker);
  yield put(Creators.deskPaymentSet({ ...marker, phase: "unconfirmed", errors, ...(checkedAgain ? { checkedAgainAt: Date.now() } : {}) }));
}

// Saves the entry as soon as the payment request is sent (so Pay is blocked
// from that moment, in every tab), then starts checking: after the request
// was accepted, after a failure that may still have reached the provider,
// or after PAYMENT_ANSWER_WAIT_MS without any answer.
export function* watchDeskPaymentRequests() {
  if (!isInRouteDeskFrame()) return;
  while (true) {
    const { data } = yield take(Types.MAKE_PAYMENT_REQUEST);
    if (!isDeskOrderPayment(data)) continue;
    const cart = yield select((state) => state.cart);
    const now = Date.now();
    // Stamped with this tab, so a reload of this tab picks it up at once.
    const record = {
      ...newInFlightRecord({ orderId: data.order_id, orderName: cart?.order_name, che: cart?.ordering_for, now }),
      pollerId: getTabId(),
      polledAt: now,
    };
    saveOne(record);
    yield put(Creators.deskPaymentSet({ ...record, phase: "checking", errors: 0 }));
    let started = false;
    let { result } = yield race({
      result: take([Types.MAKE_PAYMENT_SUCCESS, Types.MAKE_PAYMENT_FAILURE]),
      slow: delay(PAYMENT_ANSWER_WAIT_MS),
    });
    if (!result) {
      // The 60 s wait already counts as the first wait: check right away.
      yield fork(deskPaymentPoll, { record, resumed: true });
      started = true;
      result = yield take([Types.MAKE_PAYMENT_SUCCESS, Types.MAKE_PAYMENT_FAILURE]);
    }
    if (result.type === Types.MAKE_PAYMENT_FAILURE && paymentRefused(result.failure)) {
      // A poll started meanwhile stops by itself once the entry is gone.
      removeOne(record.orderId);
      yield put(Creators.deskPaymentClear(record.orderId));
      continue;
    }
    if (!started) yield fork(deskPaymentPoll, { record, resumed: false });
  }
}

// "Check again now" on a payment whose checking ended without an answer,
// or on a "paid, order not confirmed" one.
export function* watchDeskPaymentCheckAgain() {
  if (!isInRouteDeskFrame()) return;
  yield takeEvery(Types.DESK_PAYMENT_CHECK_AGAIN, function* checkAgain({ orderId }) {
    const record = loadOne(orderId);
    if (!record || (record.paid && !record.unconfirmed)) return;
    yield fork(deskPaymentPoll, { record, resumed: true, once: !!record.ended });
  });
}

// At app start: carry on with payments saved before a reload (this tab's
// own, or ones nobody is checking); afterwards, every ORPHAN_SCAN_MS, take
// over any payment whose checking tab was closed. No request is sent by
// the scan itself.
export function* resumeDeskPayments() {
  if (!isInRouteDeskFrame()) return;
  const now = Date.now();
  const tabId = getTabId();
  for (const record of loadAll()) {
    const decision = resumeDecision(record, now);
    if (decision === "resume") {
      if (needsPoller(record, now, tabId)) yield fork(deskPaymentPoll, { record, resumed: true });
    } else if (decision === "stale-show") {
      removeOne(record.orderId);
      yield put(Creators.deskPaymentSet({ ...record, phase: "stale", errors: 0 }));
    } else if (decision === "stale-quiet") {
      removeOne(record.orderId);
    } else if (decision === "keep" && record.ended && !record.finalShown && (!record.paid || record.unconfirmed)) {
      // An unclear end the officer has not closed yet: show it again.
      yield put(Creators.deskPaymentSet({ ...record, phase: record.endPhase || "timeout", errors: 0 }));
    }
    // Otherwise "keep" (closed unclear end, or a paid marker) only blocks Pay.
  }
  while (true) {
    yield delay(ORPHAN_SCAN_MS);
    const at = Date.now();
    for (const record of loadAll()) {
      // This tab's own entries are checked by watchDeskPaymentRequests (it
      // may still be waiting for the payment request's answer).
      if (record.pollerId !== tabId && !polling.has(record.orderId) && needsPoller(record, at, tabId)) {
        yield fork(deskPaymentPoll, { record, resumed: true });
      }
    }
  }
}

// The panel's screen was closed on an unclear end: don't show it again
// after a reload (the entry and the Pay block stay).
export const markFinalShown = (deskPayment) => {
  if (deskPayment) updateOne(deskPayment.orderId, { finalShown: true });
};
